import type { RowData } from '@tanstack/react-table';
import type { Column } from '@tanstack/table-core';
import { useMemo, useState } from 'react';
import type { kkTableFeatures } from '../tableFeatures';
import { type FilterOption, getUniqueFilterOptions } from './filterOptions';

interface UseImmediateCheckboxFilterProps<TData extends RowData> {
  data: TData[];
  column: Column<typeof kkTableFeatures, TData, unknown> | undefined;
  filterValue: unknown;
  showSearch: boolean;
}

interface UseImmediateCheckboxFilterResult {
  search: string;
  onSearchChange: (value: string) => void;
  options: FilterOption<unknown>[];
  selectedValues: Set<string>;
  hasVisibleOptions: boolean;
  hasAllVisibleOptionsSelected: boolean;
  isSelectAllIndeterminate: boolean;
  onToggleOption: (value: string) => void;
  onToggleVisibleOptions: () => void;
}

function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}

/** Keeps the immediate filter's option and selection state together. */
export function useImmediateCheckboxFilter<TData extends RowData>({
  data,
  column,
  filterValue,
  showSearch,
}: UseImmediateCheckboxFilterProps<TData>): UseImmediateCheckboxFilterResult {
  const [search, setSearch] = useState<string>('');
  const values: string[] = useMemo(
    () =>
      Array.isArray(filterValue) &&
      filterValue.every((value) => typeof value === 'string')
        ? filterValue
        : [],
    [filterValue],
  );
  const selectedValues = useMemo(() => new Set(values), [values]);

  const options = useMemo(() => {
    const uniqueOptions = getUniqueFilterOptions(data, column);
    const sortedOptions =
      typeof column?.columnDef.meta?.filterOptionSortFn === 'function'
        ? uniqueOptions.sort(
            (a, b) =>
              column.columnDef.meta?.filterOptionSortFn?.(
                a.original,
                b.original,
              ) ?? 0,
          )
        : uniqueOptions;

    if (search === '' || showSearch === false) return sortedOptions;

    return sortedOptions.filter(
      ({ original, value }) =>
        value.toLowerCase().includes(search.toLowerCase()) ||
        column?.columnDef.meta
          ?.filterOptionLabel?.(original, column)
          ?.toLowerCase()
          .includes(search.toLowerCase()),
    );
  }, [data, column, search, showSearch]);

  const optionValues = useMemo(
    () => options.map((option) => option.value).filter(Boolean),
    [options],
  );
  const selectedVisibleCount = optionValues.filter((value) =>
    selectedValues.has(value),
  ).length;
  const hasVisibleOptions = optionValues.length > 0;
  const hasSelectedVisibleOptions = selectedVisibleCount > 0;
  const hasAllVisibleOptionsSelected =
    hasVisibleOptions && selectedVisibleCount === optionValues.length;
  const isSelectAllIndeterminate =
    hasSelectedVisibleOptions && !hasAllVisibleOptionsSelected;

  const setFilterValues = (nextValues: string[]): void => {
    column?.setFilterValue(uniqueValues(nextValues));
  };

  const onToggleOption = (value: string): void => {
    if (selectedValues.has(value)) {
      setFilterValues(
        values.filter((selectedValue) => selectedValue !== value),
      );
      return;
    }

    setFilterValues([...values, value]);
  };

  const onToggleVisibleOptions = (): void => {
    if (hasAllVisibleOptionsSelected) {
      setFilterValues(
        values.filter((selectedValue) => !optionValues.includes(selectedValue)),
      );
      return;
    }

    setFilterValues([...values, ...optionValues]);
  };

  return {
    search,
    onSearchChange: setSearch,
    options,
    selectedValues,
    hasVisibleOptions,
    hasAllVisibleOptionsSelected,
    isSelectAllIndeterminate,
    onToggleOption,
    onToggleVisibleOptions,
  };
}
